import {
  Box,
  ChakraAsyncSelect,
  ChakraSelect,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Text,
  Textarea,
  useCustomToast,
} from 'design-system';
import { EMPTY_BRAND_OPTIONS, useGetBrandOptionsApi } from 'apis/product-brands';
import { SalesLookupVariant, useGetSalesLookupFamilyApi, useSearchLookupSuppliersApi } from 'apis/sales-lookup';
import { usePageParams } from 'hooks/use-page-params';
import { FlexibilityToggles } from 'pages/sales-lookup/components/flexibility-toggles';
import { MobileShell } from 'pages/sales-lookup/components/mobile-shell';
import { useBasket } from 'pages/sales-lookup/hooks/use-basket';
import { BasketItem, newBasketKey } from 'pages/sales-lookup/utils/basket';
import { defaultLogPriceFormValues, FlexibilityFlags, LogPriceFormValues } from 'pages/sales-lookup/utils/build-inquiry-payload';
import { fullUomLabel } from 'pages/sales-lookup/utils/full-uom-label';
import { buildVariantPricing, HeadlinePrice } from 'pages/sales-lookup/utils/headline';
import { reasonOptions } from 'pages/sales-lookup/utils/price-log-labels';
import { resolveLoggedVariant, variantLabel } from 'pages/sales-lookup/utils/variants';
import { formatPrice } from 'pages/prices/utils/matrix-display';
import qs from 'query-string';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FiChevronLeft } from 'react-icons/fi';
import { useHistory, useParams } from 'react-router-dom';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';

// What the form needs to render, resolved either from a basket snapshot (edit)
// or from the family read model (add).
interface FormSource {
  familyName: string;
  variant: SalesLookupVariant;
  headline: HeadlinePrice | null;
  initialValues: LogPriceFormValues;
}

export const PriceFeedbackScreen = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const toast = useCustomToast();
  const { familyId: familyIdParam } = useParams<{ familyId: string }>();
  const familyId = Number(familyIdParam) || undefined;

  const { getPageParams } = usePageParams();
  const params = getPageParams();
  const restaurantId = Number(params.restaurantId) || undefined;
  const restaurantName = (params.restaurantName as string) || '';
  const variantIdParam = Number(params.variantId) || undefined;
  const basketItemKey = (params.basketItem as string) || undefined;

  useEffect(() => {
    dispatch(changeTitle('Sales Lookup'));
  }, []);

  const basket = useBasket();
  const editing = basketItemKey ? basket.findByKey(basketItemKey) : undefined;

  // Editing works off the basket snapshot, so a family fetch is only needed
  // when adding a fresh item.
  const { data, isLoading } = useGetSalesLookupFamilyApi(editing ? undefined : familyId, restaurantId, {
    keepPreviousData: true,
  });
  const detail = data?.data;

  const source: FormSource | null = useMemo(() => {
    if (editing) {
      return {
        familyName: editing.familyName,
        variant: editing.variant,
        headline: editing.headline,
        initialValues: editing.values,
      };
    }
    if (!detail) return null;
    const variant = resolveLoggedVariant(detail.variants, detail.closed, variantIdParam);
    if (!variant) return null;
    return {
      familyName: detail.universalName,
      variant,
      headline: buildVariantPricing(detail, variant.id)?.headline ?? null,
      initialValues: defaultLogPriceFormValues(variant),
    };
  }, [editing, detail, variantIdParam]);

  const carriedParams = () => {
    const { restaurantId: rid, restaurantName: rname, vat, search } = getPageParams();
    return { restaurantId: rid, restaurantName: rname, vat, search };
  };

  const goToBasket = (replace = false) => {
    const location = {
      pathname: '/app/sales-lookup/basket',
      search: qs.stringify(carriedParams(), { skipEmptyString: true }),
    };
    if (replace) history.replace(location);
    else history.push(location);
  };

  const goToFamily = (variantId: number | undefined, replace = false) => {
    const location = {
      pathname: `/app/sales-lookup/families/${familyId}`,
      search: qs.stringify({ ...carriedParams(), variantId }, { skipEmptyString: true }),
    };
    if (replace) history.replace(location);
    else history.push(location);
  };

  const goBack = () => (editing ? goToBasket() : goToFamily(variantIdParam));

  const onSubmit = (values: LogPriceFormValues) => {
    if (!source) return;
    const item: BasketItem = {
      key: editing?.key ?? newBasketKey(),
      familyId: editing?.familyId ?? (familyId as number),
      familyName: source.familyName,
      variant: source.variant,
      headline: source.headline,
      values,
      addedAt: editing?.addedAt ?? new Date().toISOString(),
    };
    basket.upsert(item);

    if (editing) {
      goToBasket(true);
      return;
    }
    // Back to the family so the rep can add the next brand straight away —
    // `replace` keeps the browser back button from re-opening a stale form.
    toast({ description: `${source.familyName} added to basket`, status: 'success' });
    goToFamily(source.variant.id, true);
  };

  return (
    <MobileShell>
      <HStack as='button' spacing='4px' mb='12px' onClick={goBack}>
        <FiChevronLeft />
        <Text fontSize='14px' fontWeight='semibold' color='gray.700'>
          {editing ? 'Back to basket' : 'Back to quote'}
        </Text>
      </HStack>

      <Text fontSize='11px' fontWeight='semibold' letterSpacing='0.12em' color='gray.500' mb='10px'>
        {editing ? 'EDIT BASKET ITEM' : 'PRICE FEEDBACK'}
      </Text>

      {basketItemKey && !editing ? (
        <Box pt='24px'>
          <Text fontSize='14px' color='gray.500' textAlign='center' mb='16px'>
            This item is no longer in the basket.
          </Text>
          <BackButton label='Open basket' onClick={() => goToBasket(true)} />
        </Box>
      ) : !editing && isLoading ? null : !source ? (
        <Text fontSize='14px' color='gray.500' textAlign='center' py='40px'>
          {detail && variantIdParam != null
            ? 'This product variant is no longer available — go back and pick it again.'
            : 'Product not found'}
        </Text>
      ) : (
        <FeedbackForm
          key={editing?.key ?? source.variant.id}
          source={source}
          restaurantName={restaurantName}
          submitLabel={editing ? 'Update item' : 'Add to basket'}
          onSubmit={onSubmit}
        />
      )}
    </MobileShell>
  );
};

const BackButton = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <Box as='button' w='full' py='12px' borderRadius='12px' bg='red.600' _hover={{ bg: 'red.700' }} onClick={onClick}>
    <Text fontSize='15px' fontWeight='bold' color='white'>
      {label}
    </Text>
  </Box>
);

// Positive-number-or-blank: every capture field is optional (stakeholder
// decision 2026-09-10), but a typed value must still make sense.
const optionalPositive = (label: string) => (value: string) => {
  if (!value.trim()) return true;
  const parsed = parseFloat(value);
  if (isNaN(parsed)) return 'Please enter a valid number.';
  return parsed > 0 || `${label} must be greater than 0.`;
};

// Keyed by basket key / variant id so defaultValues re-seed when the target
// changes under a mounted instance.
const FeedbackForm = ({
  source,
  restaurantName,
  submitLabel,
  onSubmit,
}: {
  source: FormSource;
  restaurantName: string;
  submitLabel: string;
  onSubmit: (values: LogPriceFormValues) => void;
}) => {
  const { familyName, variant, headline, initialValues } = source;
  const priceUnit = headline?.unit ?? variant.packUnit ?? variant.itemUnit ?? variant.baseUnit;

  const { loadOptions: loadSupplierOptions } = useSearchLookupSuppliersApi();

  // Brand dropdown: the managed dictionary's active names, plus the variant's
  // own (possibly legacy) brand, plus a synthesized `Add "x"` option when the
  // typed text matches nothing — picking it uses the custom value in THIS log
  // only; it never writes to the dictionary.
  const { data: brandData } = useGetBrandOptionsApi();
  const brandNames = brandData ?? EMPTY_BRAND_OPTIONS;
  const [brandInput, setBrandInput] = useState('');
  const brandSelectOptions = useMemo(() => {
    const known = new Set(brandNames.map((name) => name.toLowerCase()));
    const options = brandNames.map((name) => ({ value: name, label: name }));
    const extras = [variant.brand, initialValues.brand?.value].filter((name): name is string => !!name);
    for (const name of extras) {
      if (known.has(name.toLowerCase())) continue;
      options.unshift({ value: name, label: name });
      known.add(name.toLowerCase());
    }
    const typed = brandInput.trim();
    if (typed && !known.has(typed.toLowerCase())) {
      options.push({ value: typed, label: `Add "${typed}"` });
    }
    return options;
  }, [brandNames, brandInput, variant.brand, initialValues.brand?.value]);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<LogPriceFormValues>({
    mode: 'onChange',
    defaultValues: initialValues,
  });

  const flexibility: FlexibilityFlags = {
    brandFlexible: watch('brandFlexible'),
    originFlexible: watch('originFlexible'),
    stateFlexible: watch('stateFlexible'),
    specFlexible: watch('specFlexible'),
    packagingFlexible: watch('packagingFlexible'),
  };

  return (
    <Box as='form' onSubmit={handleSubmit(onSubmit)} noValidate>
      <Text fontSize='15px' color='gray.800' mb='14px'>
        Capture what{' '}
        <Text as='span' fontWeight='bold'>
          {restaurantName || 'the customer'}
        </Text>{' '}
        pays today for{' '}
        <Text as='span' fontWeight='bold'>
          {familyName}
        </Text>{' '}
        so pricing and supply can act on it. Every field is optional.
      </Text>

      <Text fontSize='12.5px' color='gray.500' mt='-8px' mb='14px'>
        {variantLabel(variant)}
      </Text>

      <Box bg='white' borderRadius='16px' border='1px solid' borderColor='gray.100' p='16px'>
        <Controller
          control={control}
          name='reason'
          render={({ field: { onChange, onBlur, value } }) => (
            <FormControl mb='12px'>
              <FormLabel fontSize='12px'>Reason they passed</FormLabel>
              <ChakraSelect options={reasonOptions} onChange={onChange} onBlur={onBlur} value={value} isClearable placeholder='—' />
            </FormControl>
          )}
        />

        <Controller
          control={control}
          name='currentSupplier'
          render={({ field: { onChange, onBlur, value } }) => (
            <FormControl mb='12px'>
              <FormLabel fontSize='12px'>Current supplier</FormLabel>
              <ChakraAsyncSelect
                loadOptions={loadSupplierOptions}
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                isClearable
                placeholder='Search suppliers…'
                noOptionsMessage={({ inputValue }: { inputValue: string }) =>
                  inputValue.trim().length < 2 ? 'Type at least 2 characters' : 'No suppliers found'
                }
              />
            </FormControl>
          )}
        />

        <Controller
          control={control}
          name='brand'
          render={({ field: { onChange, onBlur, value } }) => (
            <FormControl mb='12px'>
              <FormLabel fontSize='12px'>Brand they buy</FormLabel>
              <ChakraSelect
                options={brandSelectOptions}
                // Normalize the synthesized `Add "x"` option so the control
                // (and the notes line) shows the clean brand name. The select's
                // onChange is typed unknown — narrow to the option shape.
                onChange={(newValue: unknown) => {
                  const option = newValue as { value: string; label: string } | null;
                  onChange(option ? { value: option.value, label: option.value } : null);
                }}
                onInputChange={(input: string) => setBrandInput(input)}
                onBlur={onBlur}
                value={value}
                isClearable
                placeholder='Select or add a brand'
              />
            </FormControl>
          )}
        />

        <FormControl mb='12px' isInvalid={!!errors.price}>
          <FormLabel fontSize='12px'>Their price / {fullUomLabel(variant, priceUnit)}</FormLabel>
          <Input
            type='number'
            step='0.01'
            min={0.01}
            inputMode='decimal'
            placeholder='Optional'
            {...register('price', { validate: optionalPositive('Price') })}
          />
          <FormErrorMessage>{errors.price?.message}</FormErrorMessage>
        </FormControl>

        <FormControl mb='12px' isInvalid={!!errors.volume}>
          <FormLabel fontSize='12px'>Volume / order</FormLabel>
          <Input
            type='number'
            step='0.5'
            min={0.5}
            inputMode='decimal'
            placeholder='Optional'
            {...register('volume', { validate: optionalPositive('Volume') })}
          />
          <FormErrorMessage>{errors.volume?.message}</FormErrorMessage>
        </FormControl>

        <FormControl mb='14px'>
          <FormLabel fontSize='12px'>Notes</FormLabel>
          <Textarea rows={3} placeholder='Anything else the pricing desk should know' {...register('notes')} />
        </FormControl>

        <FlexibilityToggles
          variant={variant}
          value={flexibility}
          onChange={(key, open) => setValue(key, open, { shouldDirty: true })}
        />

        {headline && (
          <Text fontSize='12.5px' color='gray.500' mt='12px'>
            {headline.kind === 'CLOSED' ? 'Last closed was' : 'Deal price is'} AED {formatPrice(headline.price)} /{' '}
            {headline.unit} (ex VAT){headline.supplierName ? ` via ${headline.supplierName}` : ''}.
          </Text>
        )}
      </Box>

      <Box
        as='button'
        type='submit'
        w='full'
        mt='16px'
        py='12px'
        borderRadius='12px'
        bg='red.600'
        _hover={{ bg: 'red.700' }}>
        <Text fontSize='15px' fontWeight='bold' color='white'>
          {submitLabel}
        </Text>
      </Box>
    </Box>
  );
};
